import React from 'react';
import Humanize from 'humanize-plus';

type Props = {
  value: number;
  locale?: string; // optional, not used by humanize-plus
  average?: boolean; // abbreviate format, like 12.3k
  mantissa?: number; // decimal places (not supported by humanize-plus)
  threshold?: number; // don't abbreviate below this
  dataUnit?: string; // optional unit, e.g., "views", "followers", etc.
  className?: string; // optional CSS class name for custom styling
  compactLength?: number; // optional, not used by humanize-plus
};

const normalizeCompactValue = (value: string) =>
  value
    .replace(/(\.\d*?)0+([a-zA-Z])$/, '$1$2')
    .replace(/\.([a-zA-Z])$/, '$1')
    .replace(/k$/, 'K');

export const LomavisShortenedNumber: React.FC<Props> = ({
  value,
  average = true,
  threshold = 10_000,
  dataUnit = '',
  className = '',
  compactLength = 1
}) => {
  const abs = Math.abs(value);
  const shouldAbbreviate = average && abs >= threshold;

  let formatted;
  if (shouldAbbreviate) {
    // For numbers that need abbreviation
    const compactValue = Humanize.compactInteger(value, compactLength); // e.g., "12.3K"

    // Check if the number can be represented without a suffix
    // For example, with compactLength=3, numbers up to 999.999 can be shown without a suffix
    const maxValueWithoutSuffix =
      Math.pow(10, 3 + compactLength) - Math.pow(10, -compactLength);

    if (abs < maxValueWithoutSuffix) {
      // Format without suffix, using the specified precision
      formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: compactLength
      });
    } else {
      formatted = normalizeCompactValue(compactValue);
    }
  } else {
    formatted = value.toLocaleString(); // fallback to readable number
  }

  return (
    <span
      data-full-value={value}
      data-numeric-value={value}
      className={className}
    >
      {formatted}
      {dataUnit && <>&nbsp;{dataUnit}</>}
    </span>
  );
};
